// resources/js/Components/zone/ScheduleSummary.jsx
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const allDays = [0, 1, 2, 3, 4, 5, 6];
const minutesInDay = 24 * 60;

/**
 * Convert HH:MM to minutes from midnight.
 */
const toMinutes = (time) => {
    if (typeof time !== 'string') return null;
    const [hours, minutes] = time.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return (hours * 60) + minutes;
};

/**
 * Build per-day interval segments, carrying overnight ranges into next day.
 */
const pushIntervalForDay = (dayIntervals, dayIndex, startMinutes, stopMinutes) => {
    if (startMinutes == null || stopMinutes == null) return;

    if (startMinutes === stopMinutes) {
        dayIntervals[dayIndex].push({ start: 0, end: minutesInDay });
        return;
    }

    if (stopMinutes > startMinutes) {
        dayIntervals[dayIndex].push({ start: startMinutes, end: stopMinutes });
        return;
    }

    // Overnight intervals (e.g. 19:00-06:00) span current and following day.
    dayIntervals[dayIndex].push({ start: startMinutes, end: minutesInDay });
    dayIntervals[(dayIndex + 1) % 7].push({ start: 0, end: stopMinutes });
};

/**
 * Merge overlapping segments to keep visual and textual output consistent.
 */
const mergeIntervals = (intervals) => {
    if (!intervals.length) return [];

    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const merged = [sorted[0]];

    for (let index = 1; index < sorted.length; index += 1) {
        const current = sorted[index];
        const last = merged[merged.length - 1];

        if (current.start <= last.end) {
            last.end = Math.max(last.end, current.end);
        } else {
            merged.push({ ...current });
        }
    }

    return merged;
};

/**
 * Format minute offsets into 24-hour labels.
 */
const formatMinuteLabel = (minute) => {
    if (minute === minutesInDay) return '24:00';

    const hours = Math.floor(minute / 60);
    const minutes = minute % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Build one human-readable interval label for a single day row.
 */
const describeDayIntervals = (intervals) => {
    if (!intervals.length) return null;

    if (intervals.length === 1 && intervals[0].start === 0 && intervals[0].end === minutesInDay) {
        return 'All day';
    }

    if (
        intervals.length === 2
        && intervals[0].start === 0
        && intervals[1].end === minutesInDay
    ) {
        return `${formatMinuteLabel(intervals[1].start)}-${formatMinuteLabel(intervals[0].end)}`;
    }

    return intervals
        .map((interval) => `${formatMinuteLabel(interval.start)}-${formatMinuteLabel(interval.end)}`)
        .join(', ');
};

/**
 * Group consecutive days sharing the same interval description.
 */
const buildSummaryText = (dayIntervals) => {
    const descriptions = dayIntervals.map((intervals) => describeDayIntervals(intervals));
    const lines = [];

    let day = 0;
    while (day < descriptions.length) {
        const currentDescription = descriptions[day];

        if (!currentDescription) {
            day += 1;
            continue;
        }

        let endDay = day;
        while (
            endDay + 1 < descriptions.length
            && descriptions[endDay + 1] === currentDescription
        ) {
            endDay += 1;
        }

        const dayLabel = day === endDay
            ? dayLabels[day]
            : `${dayLabels[day]}-${dayLabels[endDay]}`;

        lines.push(`${dayLabel}: ${currentDescription}`);
        day = endDay + 1;
    }

    return lines.join('; ');
};

/**
 * Render one bar row over a 24h axis for a day of intervals.
 */
const DayTimeline = ({ day, intervals }) => (
    <Box display="flex" alignItems="center" gap={1.5}>
        <Typography variant="caption" sx={{ width: 36, color: 'text.secondary' }}>
            {day}
        </Typography>
        <Box
            sx={{
                position: 'relative',
                flex: 1,
                height: 16,
                borderRadius: 1,
                bgcolor: 'action.hover',
                overflow: 'hidden',
            }}
        >
            {[6, 12, 18].map((hour) => (
                <Box
                    // Light guide lines to make start/stop positions easier to read.
                    key={hour}
                    sx={{
                        position: 'absolute',
                        left: `${(hour / 24) * 100}%`,
                        top: 0,
                        bottom: 0,
                        width: 1,
                        bgcolor: 'divider',
                    }}
                />
            ))}
            {intervals.map((interval, index) => {
                const left = (interval.start / minutesInDay) * 100;
                const width = ((interval.end - interval.start) / minutesInDay) * 100;

                return (
                    <Box
                        key={`${day}-${interval.start}-${interval.end}-${index}`}
                        sx={{
                            position: 'absolute',
                            left: `${left}%`,
                            top: 0,
                            height: '100%',
                            width: `${Math.max(width, 1)}%`,
                            bgcolor: 'primary.main',
                            opacity: 0.9,
                        }}
                    />
                );
            })}
        </Box>
    </Box>
);

const ScheduleSummary = ({ schedule, showTimeline = false, emptyLabel = 'Always armed' }) => {
    if (!schedule || schedule.length === 0) {
        return <Typography variant="body2">{emptyLabel}</Typography>;
    }

    const dayIntervals = allDays.map(() => []);
    schedule.forEach(({ start, stop, days = [] }) => {
        const startMinutes = toMinutes(start);
        const stopMinutes = toMinutes(stop);
        const targetDays = days.length > 0 ? days : allDays;

        targetDays.forEach((dayIndex) => {
            pushIntervalForDay(dayIntervals, dayIndex, startMinutes, stopMinutes);
        });
    });

    const normalizedDayIntervals = dayIntervals.map((intervals) => mergeIntervals(intervals));
    const summaryText = buildSummaryText(normalizedDayIntervals);

    return (
        <Box mt={1}>
            <Typography variant="body2" fontWeight="bold">Schedule:</Typography>
            {showTimeline && (
                <>
                    <Stack spacing={0.75} mt={1.25}>
                        {dayLabels.map((day, index) => (
                            <DayTimeline key={day} day={day} intervals={normalizedDayIntervals[index]} />
                        ))}
                    </Stack>
                    <Box display="flex" justifyContent="space-between" mt={0.75}>
                        <Typography variant="caption" color="text.secondary">00:00</Typography>
                        <Typography variant="caption" color="text.secondary">06:00</Typography>
                        <Typography variant="caption" color="text.secondary">12:00</Typography>
                        <Typography variant="caption" color="text.secondary">18:00</Typography>
                        <Typography variant="caption" color="text.secondary">24:00</Typography>
                    </Box>
                </>
            )}
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {summaryText || 'No active windows'}
            </Typography>
        </Box>
    );
};

export default ScheduleSummary;
